/**
 * Paso 3 de la activación (T033): registrar este dispositivo.
 *
 * Al confirmar se crea (si no existía) la clave ECDSA P-256 **no exportable** del teléfono y se
 * envía su parte pública a `POST /activation/{token}/enroll`, firmando la petición con ella y sin
 * `id=` (el servidor deriva el `device_id` de la clave). La respuesta trae la cookie de sesión, así
 * que a partir de aquí el trabajador queda autenticado sin volver a escribir credenciales (FR-011).
 *
 * Errores del contrato: `409` lo resuelve `enrollDevice` (descarta la clave y reintenta una vez);
 * `410` vuelve a la pantalla de enlace inválido; `428` regresa al aviso de privacidad (o, si es
 * `DOCUMENT_VERIFICATION_REQUIRED`, al enlace para verificar el documento, 005 · QA-6);
 * `DEVICE_CLOCK_SKEW` pide revisar la hora del teléfono.
 */
import { KeyRound } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Card, CardDescription, CardTitle } from '../../components/ui/card';
import { Screen } from '../../components/ui/screen';
import { Stepper } from '../../components/ui/stepper';
import { t } from '../../i18n';

import { useEnrollDevice } from './api';
import { isInvitationGone } from './link-invalid';

import type { ApiError } from '../../api/client';

export interface EnrollScreenProps {
  token: string;
  onEnrolled: () => void;
  onGone: () => void;
  /** `428 PRIVACY_NOTICE_REQUIRED`: falta aceptar el aviso antes de registrar. */
  onPrivacyRequired: () => void;
  /** 005 · QA-6 · `428 DOCUMENT_VERIFICATION_REQUIRED`: vuelve al enlace a verificar el documento. */
  onVerificationRequired?: () => void;
}

/** Mensaje en pantalla para cada error del contrato (los textos viven en `activation.json`). */
export function enrollErrorMessage(error: ApiError): string {
  if (error.is('CONFLICT')) {
    return t('activation.enroll.conflict');
  }
  if (error.is('DEVICE_CLOCK_SKEW')) {
    return t('activation.enroll.clockSkew');
  }
  return error.message || t('activation.enroll.error');
}

export function EnrollScreen({
  token,
  onEnrolled,
  onGone,
  onPrivacyRequired,
  onVerificationRequired,
}: EnrollScreenProps) {
  const enroll = useEnrollDevice(token);

  function submit(): void {
    enroll.mutate(undefined, {
      onSuccess: onEnrolled,
      onError: (error) => {
        if (isInvitationGone(error)) {
          onGone();
          return;
        }
        if (error.is('PRIVACY_NOTICE_REQUIRED')) {
          onPrivacyRequired();
          return;
        }
        if (error.is('DOCUMENT_VERIFICATION_REQUIRED')) {
          onVerificationRequired?.();
        }
      },
    });
  }

  const error = enroll.error;
  const showError =
    error !== null &&
    !isInvitationGone(error) &&
    !error.is('PRIVACY_NOTICE_REQUIRED') &&
    !(error.is('DOCUMENT_VERIFICATION_REQUIRED') && onVerificationRequired !== undefined);

  return (
    <Screen
      data-testid="activation-enroll"
      header={<Stepper current={3} />}
      title={t('activation.enroll.title')}
      lead={t('activation.enroll.lead')}
      footer={
        <>
          {showError ? (
            <p role="alert" className="text-fm-sm text-fm-danger-text">
              {enrollErrorMessage(error)}
            </p>
          ) : null}
          <Button
            loading={enroll.isPending}
            loadingText={t('activation.enroll.loading')}
            icon={<KeyRound aria-hidden="true" size={20} />}
            onClick={submit}
          >
            {t('activation.enroll.action')}
          </Button>
        </>
      }
    >
      <Card>
        <CardTitle>{t('activation.enroll.cardTitle')}</CardTitle>
        <CardDescription>{t('activation.enroll.cardDescription')}</CardDescription>
      </Card>
    </Screen>
  );
}
